import { create } from 'zustand'
import { DemoLead } from '@forwardflow/types/demo-conversation'
import { DemoConversationService } from '@forwardflow/services/DemoConversation'

interface LeadConversationData {
  timelineHistory: any[]
  lastUpdate: string | null
  recentConversation: string | null
  currentStepIndex: number
  leadDetails: DemoLead | null
  currentCampaign: string | null
  currentCampaignType: string | null
  preDefinedTimelineHistory: any[]
  initialCampaignPhase: string | null
  initialTimelineSummary: string | null
  initialQuestions: string | null
}

interface DemoConversationStore {
  leadConversations: Record<number, LeadConversationData>
  savingStates: Record<string, boolean>

  ensureLeadConversationData: (leadId: number) => void

  updateTimelineHistory: (leadId: number, data: any[]) => void
  initializeTimelineHistory: (leadId: number, data: any[]) => void
  setLastUpdate: (leadId: number, timestamp: string) => void
  setRecentConversation: (leadId: number, conversation: string | null, type?: string) => void
  setCurrentStepIndex: (leadId: number, stepIndex: number) => void
  getLeadConversationData: (leadId: number) => LeadConversationData | null
  getTimelineHistory: (leadId: number) => any[]

  setLeadDetails: (leadId: number, details: DemoLead) => void
  updateLeadDetails: (leadId: number, updates: Partial<DemoLead>) => void
  getLeadDetails: (leadId: number) => DemoLead | null

  incrementMessagesSent: (leadId: number) => void
  incrementMessagesReplied: (leadId: number) => void
  incrementCallsAttempted: (leadId: number) => void
  incrementCallsAnswered: (leadId: number) => void

  updateAppointmentStatus: (lleadId: number, status: string | null, date?: string | null, platform?: string | null) => void

  updateStage: (leadId: number, stageId: number, stageName: string) => void
  updateTimelineSummary: (leadId: number, summary: string) => void
  updateQuestions: (leadId: number, questions: string) => void

  addTag: (leadId: number, tag: string) => void
  removeTag: (leadId: number, tag: string) => void

  setCurrentCampaign: (leadId: number, stage: string | null) => void
  setCurrentCampaignType: (leadId: number, type: string | null) => void

  setInitialValues: (leadId: number, phase: string | null, summary: string | null, questions: string | null) => void
  getInitialValues: (leadId: number) => { phase: string | null, summary: string | null, questions: string | null }

  getEffectiveStage: (leadId: number) => string | null

  clearLeadConversation: (leadId: number) => void
  getAllLeadConversations: () => Record<number, LeadConversationData>

  getNextStepInfo: (leadId: number) => { campaignType: string; campaignPhase: string; step: number } | null

  saveTimelineUpToStep: (leadId: number, stepIndex: number) => Promise<boolean>;
  saveTimelineHistory: (leadId: number) => Promise<boolean>
  saveTimelineStep: (leadId: number, stepIndex: number) => Promise<boolean>
  updatePreDefinedTimelineHistory: (leadId: number, data: any[]) => void
  getPreDefinedTimelineHistory: (leadId: number) => any[]
  isSaving: (key: string) => boolean
  setSaving: (key: string, saving: boolean) => void
}

export const useDemoConversationStore = create<DemoConversationStore>((set, get) => ({
  leadConversations: {},
  savingStates: {},

  ensureLeadConversationData: (leadId: number) => {
    set((state) => {
      if (!state.leadConversations[leadId]) {
        return {
          leadConversations: {
            ...state.leadConversations,
            [leadId]: {
              timelineHistory: [],
              lastUpdate: new Date().toISOString(),
              recentConversation: null,
              currentStepIndex: 0,
              leadDetails: null,
              currentCampaign: null,
              currentCampaignType: null,
              preDefinedTimelineHistory: [],
              initialCampaignPhase: null,
              initialTimelineSummary: null,
              initialQuestions: null
            }
          }
        }
      }
      return state
    })
  },

  setInitialValues: (leadId: number, phase: string | null, summary: string | null, questions: string | null) => {
    get().ensureLeadConversationData(leadId)
    set((state) => ({
      leadConversations: {
        ...state.leadConversations,
        [leadId]: {
          ...state.leadConversations[leadId],
          initialCampaignPhase: phase,
          initialTimelineSummary: summary,
          initialQuestions: questions,
          lastUpdate: new Date().toISOString()
        }
      }
    }))
  },

  getInitialValues: (leadId: number) => {
    const state = get()
    const leadData = state.leadConversations[leadId]
    return {
      phase: leadData?.initialCampaignPhase || null,
      summary: leadData?.initialTimelineSummary || null,
      questions: leadData?.initialQuestions || null
    }
  },

  updateTimelineHistory: (leadId: number, data: any[]) => {
    set((state) => ({
      leadConversations: {
        ...state.leadConversations,
        [leadId]: {
          ...state.leadConversations[leadId],
          timelineHistory: data,
          lastUpdate: new Date().toISOString(),
          currentStepIndex: data.length,
          recentConversation: state.leadConversations[leadId]?.recentConversation || null,
          leadDetails: state.leadConversations[leadId]?.leadDetails || null,
          currentCampaign: state.leadConversations[leadId]?.currentCampaign || null,
          currentCampaignType: state.leadConversations[leadId]?.currentCampaignType || null,
          preDefinedTimelineHistory: state.leadConversations[leadId]?.preDefinedTimelineHistory || [],
          initialCampaignPhase: state.leadConversations[leadId]?.initialCampaignPhase || null,
          initialTimelineSummary: state.leadConversations[leadId]?.initialTimelineSummary || null,
          initialQuestions: state.leadConversations[leadId]?.initialQuestions || null
        }
      }
    }))
  },

  initializeTimelineHistory: (leadId: number, data: any[]) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (existingData?.timelineHistory?.length > 0) {
        return state
      }

      const lastItem = data.length > 0 ? data[data.length - 1] : null
      const recentConversation = lastItem?.content || null

      const firstTimelineItem = data.length > 0 ? data[0] : null
      const initialCampaignPhase = firstTimelineItem?.campaignPhase || null

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            timelineHistory: [...data],
            lastUpdate: new Date().toISOString(),
            currentStepIndex: data.length,
            recentConversation: recentConversation,
            leadDetails: existingData?.leadDetails || null,
            currentCampaign: existingData?.currentCampaign || null,
            currentCampaignType: existingData?.currentCampaignType || null,
            preDefinedTimelineHistory: [...data],
            initialCampaignPhase: initialCampaignPhase,
            initialTimelineSummary: existingData?.initialTimelineSummary || null,
            initialQuestions: existingData?.initialQuestions || null
          }
        }
      }
    })
  },

  getTimelineHistory: (leadId: number) => {
    const state = get()
    const leadData = state.leadConversations[leadId]

    if (!leadData) return []

    return leadData.timelineHistory || []
  },

  getNextStepInfo: (leadId: number) => {
    const state = get()
    const leadData = state.leadConversations[leadId]

    if (!leadData) return null

    const timelineHistory = leadData.timelineHistory || []
    const currentCampaignPhase = leadData.currentCampaign

    if (!currentCampaignPhase || timelineHistory.length === 0) {
      return {
        campaignType: leadData.currentCampaignType || 'demo-seller',
        campaignPhase: currentCampaignPhase || 'ff-lead',
        step: 1
      }
    }

    const itemsInCurrentPhase = timelineHistory.filter(item =>
      item.campaignPhase === currentCampaignPhase
    )

    if (itemsInCurrentPhase.length === 0) {
      return {
        campaignType: leadData.currentCampaignType || 'demo-seller',
        campaignPhase: currentCampaignPhase,
        step: 1
      }
    }

    const maxStep = Math.max(...itemsInCurrentPhase.map(item => item.step || 0))

    return {
      campaignType: leadData.currentCampaignType || 'demo-seller',
      campaignPhase: currentCampaignPhase,
      step: maxStep + 1
    }
  },

  setLastUpdate: (leadId: number, timestamp: string) => {
    set((state) => ({
      leadConversations: {
        ...state.leadConversations,
        [leadId]: {
          timelineHistory: state.leadConversations[leadId]?.timelineHistory || [],
          recentConversation: state.leadConversations[leadId]?.recentConversation || null,
          currentStepIndex: state.leadConversations[leadId]?.currentStepIndex || 0,
          lastUpdate: timestamp,
          leadDetails: state.leadConversations[leadId]?.leadDetails || null,
          currentCampaign: state.leadConversations[leadId]?.currentCampaign || null,
          currentCampaignType: state.leadConversations[leadId]?.currentCampaignType || null,
          preDefinedTimelineHistory: state.leadConversations[leadId]?.preDefinedTimelineHistory || [],
          initialCampaignPhase: state.leadConversations[leadId]?.initialCampaignPhase || null,
          initialTimelineSummary: state.leadConversations[leadId]?.initialTimelineSummary || null,
          initialQuestions: state.leadConversations[leadId]?.initialQuestions || null
        }
      }
    }))
  },

  setRecentConversation: (leadId: number, conversation: string | null, type = 'demo-seller') => {
    set((state) => ({
      leadConversations: {
        ...state.leadConversations,
        [leadId]: {
          timelineHistory: state.leadConversations[leadId]?.timelineHistory || [],
          recentConversation: conversation,
          currentStepIndex: state.leadConversations[leadId]?.currentStepIndex || 0,
          lastUpdate: new Date().toISOString(),
          leadDetails: state.leadConversations[leadId]?.leadDetails || null,
          currentCampaign: state.leadConversations[leadId]?.currentCampaign || null,
          currentCampaignType: state.leadConversations[leadId]?.currentCampaignType || type,
          preDefinedTimelineHistory: state.leadConversations[leadId]?.preDefinedTimelineHistory || [],
          initialCampaignPhase: state.leadConversations[leadId]?.initialCampaignPhase || null,
          initialTimelineSummary: state.leadConversations[leadId]?.initialTimelineSummary || null,
          initialQuestions: state.leadConversations[leadId]?.initialQuestions || null
        }
      }
    }))
  },

  setCurrentStepIndex: (leadId: number, stepIndex: number) => {
    set((state) => ({
      leadConversations: {
        ...state.leadConversations,
        [leadId]: {
          timelineHistory: state.leadConversations[leadId]?.timelineHistory || [],
          recentConversation: state.leadConversations[leadId]?.recentConversation || null,
          currentStepIndex: stepIndex,
          lastUpdate: state.leadConversations[leadId]?.lastUpdate || new Date().toISOString(),
          leadDetails: state.leadConversations[leadId]?.leadDetails || null,
          currentCampaign: state.leadConversations[leadId]?.currentCampaign || null,
          currentCampaignType: state.leadConversations[leadId]?.currentCampaignType || null,
          preDefinedTimelineHistory: state.leadConversations[leadId]?.preDefinedTimelineHistory || [],
          initialCampaignPhase: state.leadConversations[leadId]?.initialCampaignPhase || null,
          initialTimelineSummary: state.leadConversations[leadId]?.initialTimelineSummary || null,
          initialQuestions: state.leadConversations[leadId]?.initialQuestions || null
        }
      }
    }))
  },

  getLeadConversationData: (leadId: number) => {
    const state = get()
    return state.leadConversations[leadId] || null
  },

  setLeadDetails: (leadId: number, details: DemoLead) => {
    set((state) => ({
      leadConversations: {
        ...state.leadConversations,
        [leadId]: {
          timelineHistory: state.leadConversations[leadId]?.timelineHistory || [],
          recentConversation: state.leadConversations[leadId]?.recentConversation || null,
          currentStepIndex: state.leadConversations[leadId]?.currentStepIndex || 0,
          lastUpdate: state.leadConversations[leadId]?.lastUpdate || new Date().toISOString(),
          leadDetails: details,
          currentCampaign: state.leadConversations[leadId]?.currentCampaign || null,
          currentCampaignType: state.leadConversations[leadId]?.currentCampaignType || null,
          preDefinedTimelineHistory: state.leadConversations[leadId]?.preDefinedTimelineHistory || [],
          initialCampaignPhase: state.leadConversations[leadId]?.initialCampaignPhase || null,
          initialTimelineSummary: state.leadConversations[leadId]?.initialTimelineSummary || null,
          initialQuestions: state.leadConversations[leadId]?.initialQuestions || null
        }
      }
    }))
  },

  updateLeadDetails: (leadId: number, updates: Partial<DemoLead>) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData?.leadDetails) {
        console.warn(`No lead details found for leadId: ${leadId}`)
        return state
      }

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            leadDetails: {
              ...existingData.leadDetails,
              ...updates
            },
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  getLeadDetails: (leadId: number) => {
    const state = get()
    return state.leadConversations[leadId]?.leadDetails || null
  },

  incrementMessagesSent: (leadId: number) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData?.leadDetails) return state

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            leadDetails: {
              ...existingData.leadDetails,
              conversationInfo: {
                ...existingData.leadDetails.conversationInfo,
                messagesSent: (existingData.leadDetails.conversationInfo?.messagesSent || 0) + 1
              }
            },
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  incrementMessagesReplied: (leadId: number) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData?.leadDetails) return state

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            leadDetails: {
              ...existingData.leadDetails,
              conversationInfo: {
                ...existingData.leadDetails.conversationInfo,
                messagesReplied: (existingData.leadDetails.conversationInfo?.messagesReplied || 0) + 1
              }
            },
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  incrementCallsAttempted: (leadId: number) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData?.leadDetails) return state

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            leadDetails: {
              ...existingData.leadDetails,
              conversationInfo: {
                ...existingData.leadDetails.conversationInfo,
                callsAttempted: (existingData.leadDetails.conversationInfo?.callsAttempted || 0) + 1
              }
            },
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  incrementCallsAnswered: (leadId: number) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData?.leadDetails) return state

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            leadDetails: {
              ...existingData.leadDetails,
              conversationInfo: {
                ...existingData.leadDetails.conversationInfo,
                callsAnswered: (existingData.leadDetails.conversationInfo?.callsAnswered || 0) + 1
              }
            },
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  updateAppointmentStatus: (leadId: number, status: string | null, date?: string | null, platform?: string | null) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData?.leadDetails) return state

      const updatedAppointmentInfo = {
        date: date !== undefined ? date : existingData.leadDetails.appointmentInfo?.date,
        platform: platform !== undefined ? platform : existingData.leadDetails.appointmentInfo?.platform,
        status: status
      }

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            leadDetails: {
              ...existingData.leadDetails,
              appointmentInfo: updatedAppointmentInfo
            },
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  updateStage: (leadId: number, stageId: number, stageName: string) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData?.leadDetails) return state

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            leadDetails: {
              ...existingData.leadDetails,
              stageInfo: {
                id: stageId,
                name: stageName
              }
            },
            currentCampaign: stageName,
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  updateTimelineSummary: (leadId: number, summary: string) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData?.leadDetails) return state

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            leadDetails: {
              ...existingData.leadDetails,
              timelineSummary: summary
            },
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  updateQuestions: (leadId: number, questions: string) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData?.leadDetails) return state

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            leadDetails: {
              ...existingData.leadDetails,
              questions: questions
            },
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  addTag: (leadId: number, tag: string) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData?.leadDetails) return state

      const currentTags = existingData.leadDetails.tags || []
      if (currentTags.includes(tag)) return state

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            leadDetails: {
              ...existingData.leadDetails,
              tags: [...currentTags, tag]
            },
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  removeTag: (leadId: number, tag: string) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData?.leadDetails) return state

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            leadDetails: {
              ...existingData.leadDetails,
              tags: (existingData.leadDetails.tags || []).filter(t => t !== tag)
            },
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  setCurrentCampaign: (leadId: number, stage: string | null) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData) {
        return {
          leadConversations: {
            ...state.leadConversations,
            [leadId]: {
              timelineHistory: [],
              recentConversation: null,
              currentStepIndex: 0,
              lastUpdate: new Date().toISOString(),
              leadDetails: null,
              currentCampaign: stage,
              currentCampaignType: null,
              preDefinedTimelineHistory: [],
              initialCampaignPhase: null,
              initialTimelineSummary: null,
              initialQuestions: null,
            }
          }
        }
      }

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            currentCampaign: stage,
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  setCurrentCampaignType: (leadId: number, type: string | null) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData) {
        return {
          leadConversations: {
            ...state.leadConversations,
            [leadId]: {
              timelineHistory: [],
              recentConversation: null,
              currentStepIndex: 0,
              lastUpdate: new Date().toISOString(),
              leadDetails: null,
              currentCampaign: null,
              currentCampaignType: type,
              preDefinedTimelineHistory: [],
              initialCampaignPhase: null,
              initialTimelineSummary: null,
              initialQuestions: null,
            }
          }
        }
      }

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            currentCampaignType: type,
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  getEffectiveStage: (leadId: number) => {
    const state = get()
    const leadData = state.leadConversations[leadId]

    if (!leadData) return null

    const timelineHistory = leadData.timelineHistory || []

    if (timelineHistory.length === 0) {
      return leadData.initialCampaignPhase
    }

    const lastStageItem = timelineHistory
      .slice()
      .reverse()
      .find(item => item.campaignPhase)

    return lastStageItem?.campaignPhase || leadData.initialCampaignPhase
  },

  clearLeadConversation: (leadId: number) => {
    set((state) => {
      const newLeadConversations = { ...state.leadConversations }
      delete newLeadConversations[leadId]
      return { leadConversations: newLeadConversations }
    })
  },

  getAllLeadConversations: () => {
    return get().leadConversations
  },

  saveTimelineHistory: async (leadId: number) => {
    const state = get()
    const leadData = state.leadConversations[leadId]

    if (!leadData || !leadData.leadDetails) {
      console.error(`No lead data or lead details found for leadId: ${leadId}`)
      return false
    }

    const saveKey = `save-timeline-details-${leadId}`

    try {
      get().setSaving(saveKey, true)

      const payload = {
        ...leadData.leadDetails,
        leadInterestType: leadData.leadDetails.leadInterestType === 'demo-buyer' ? 'Buyer'
          : leadData.leadDetails.leadInterestType === 'demo-seller' ? 'Seller' : leadData.leadDetails.leadInterestType,
        preDefinedTimelineHistory: leadData.timelineHistory
      }

      await DemoConversationService.createOrUpdateLeadDetails(payload)

      get().updatePreDefinedTimelineHistory(leadId, leadData.timelineHistory)

      if (leadData.leadDetails) {
        get().setLeadDetails(leadId, leadData.leadDetails)
      }

      return true
    } catch (error) {
      console.error('Error saving timeline and lead details:', error)
      return false
    } finally {
      get().setSaving(saveKey, false)
    }
  },

  saveTimelineUpToStep: async (leadId: number, stepIndex: number) => {
    const state = get();
    const currentTimeline = state.getTimelineHistory(leadId);
    const preDefinedTimeline = state.getPreDefinedTimelineHistory(leadId);

    if (!currentTimeline || stepIndex >= currentTimeline.length) return false;

    const currentTimelineUpToStep = currentTimeline.slice(0, stepIndex + 1);
    const preDefinedTimelineUpToStep = preDefinedTimeline.slice(0, stepIndex + 1);

    const hasChanges = currentTimelineUpToStep.some((currentItem, index) => {
      const preDefinedItem = preDefinedTimelineUpToStep[index];

      if (!preDefinedItem) return true;

      return (
        currentItem.itemType !== preDefinedItem.itemType ||
        currentItem.isIncoming !== preDefinedItem.isIncoming ||
        currentItem.isActiveConversationItem !== preDefinedItem.isActiveConversationItem
      );
    });

    const hasNewItems = currentTimelineUpToStep.length > preDefinedTimelineUpToStep.length;

    if (hasChanges || hasNewItems) {
      return state.saveTimelineHistory(leadId);
    } else {
      return state.saveTimelineStep(leadId, stepIndex);
    }
  },

  saveTimelineStep: async (leadId: number, stepIndex: number) => {
    const state = get()
    const leadData = state.leadConversations[leadId]

    if (!leadData || !leadData.timelineHistory[stepIndex]) {
      console.error(`No step found at index ${stepIndex} for leadId: ${leadId}`)
      return false
    }

    const saveKey = `save-step-${leadId}-${stepIndex}`

    try {
      get().setSaving(saveKey, true)
      const stepData = leadData.timelineHistory[stepIndex]
      const currentPredefined = leadData.preDefinedTimelineHistory || []
      const newPredefined = [...currentPredefined]

      while (newPredefined.length <= stepIndex) {
        if (newPredefined.length < leadData.timelineHistory.length) {
          newPredefined.push(leadData.timelineHistory[newPredefined.length])
        } else {
          break
        }
      }
      newPredefined[stepIndex] = stepData
      await DemoConversationService.updateLeadPreDefinedTimelineHistory(
        leadId,
        newPredefined
      )

      get().updatePreDefinedTimelineHistory(leadId, newPredefined)

      return true
    } catch (error) {
      console.error('Error saving timeline step:', error)
      return false
    } finally {
      get().setSaving(saveKey, false)
    }
  },

  updatePreDefinedTimelineHistory: (leadId: number, data: any[]) => {
    set((state) => {
      const existingData = state.leadConversations[leadId]
      if (!existingData) return state

      return {
        leadConversations: {
          ...state.leadConversations,
          [leadId]: {
            ...existingData,
            preDefinedTimelineHistory: data,
            lastUpdate: new Date().toISOString()
          }
        }
      }
    })
  },

  getPreDefinedTimelineHistory: (leadId: number) => {
    const state = get()
    const leadData = state.leadConversations[leadId]
    return leadData?.preDefinedTimelineHistory || []
  },

  isSaving: (key: string) => {
    const state = get()
    return state.savingStates[key] || false
  },

  setSaving: (key: string, saving: boolean) => {
    set((state) => ({
      savingStates: {
        ...state.savingStates,
        [key]: saving
      }
    }))
  }
}))